//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { IPromotedPostsMetricsState } from 'app/promoted-posts-metrics/models/AppState';
import { getParsedStatus } from 'app/promoted-posts-metrics/promoted-posts-metrics-helper';
import { PROMOTED_POSTS_METRICS_ACTIONS } from 'app/promoted-posts-metrics/promoted-posts-metrics-constants/promoted-posts-metrics-constants';
import {
  PROMOTED_POSTS_METRICS_SORT_DIR,
  PROMOTED_POSTS_METRICS_SORT_KEY,
} from 'app/promoted-posts-metrics/promoted-posts-metrics-table/promoted-posts-metrics-table-values';
import { PROMOTED_POSTS_TREND_VALUES } from 'app/promoted-posts-metrics/promoted-posts-metrics-trends/promoted-posts-metrics-trends-values';

const initialState: IPromotedPostsMetricsState = {
  totalPostCount: 0, // TODO: Hook this up to the actions
  dateRange: {
    alias: 'last7Days',
    startDate: moment().startOf('day').subtract(6, 'd').toDate(),
    endDate: moment().startOf('day').toDate(),
  },
  hasPromotedPosts: true,
  isOnboarded: false,
  onboardingStepsState: {
    hasFacebookUserConnected: false,
    hasFacebookConnected: false,
    hasIgAccountPaired: false,
    hasAdsPermissions: false,
    tokensHaveSameUser: false,
    facebookUserTokenEntityName: null,
    facebookAdsTokenEntityName: null,
  },
  failedToFetchOnboardingState: false,
  fetchingOnboardingState: true,
  isFetchingAggregateStats: true,
  isFetchingTrendStats: true,
  isFetchingPromotedPostsInRange: true,

  aggregateStats: [], // TODO: Hook this up to the actions

  trendStats: [], // TODO: Hook this up to the actions
  trendSelectedValue: PROMOTED_POSTS_TREND_VALUES.TOTAL_CLICKS,

  promotedPostsStats: [],
  promotedPostsStatsSortKey: PROMOTED_POSTS_METRICS_SORT_KEY.PROMOTION_SCHEDULE,
  promotedPostsStatsSortDir: PROMOTED_POSTS_METRICS_SORT_DIR.DESC,
  promotedPostsStatsOffset: 0,
  promotedPostsStatsLimit: 10,

  recentPromotedPostsStats: [],
  isFetchingRecentPromotions: true,
  isUpdatingInsightsForPost: false,
  isFetchingRunningAds: false,
  runningPromotionPostIds: new Set(),
  promotionStates: {},

  isGaConnected: true,
  isGaEcommerceConnected: true,
};

export default function PromotedPostsReducer(
  state: IPromotedPostsMetricsState = initialState,
  action,
): IPromotedPostsMetricsState {
  switch (action.type) {
    case PROMOTED_POSTS_METRICS_ACTIONS.FETCH_ONBOARDING_STEPS_STATE:
      return {
        ...state,
        fetchingOnboardingState: true,
        failedToFetchOnboardingState: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_ONBOARDING_STEPS_STATE_SUCCEEDED:
      return {
        ...state,
        onboardingStepsState: action.payload.stepsState,
        isOnboarded: action.payload.isOnboarded,
        fetchingOnboardingState: false,
        failedToFetchOnboardingState: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_ONBOARDING_STEPS_STATE_FAILED:
      return {
        ...state,
        fetchingOnboardingState: false,
        failedToFetchOnboardingState: true,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_GA_STATE:
      return {
        ...state,
        isGaConnected: action.payload.isGaConnected,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_DATE_RANGE:
      return {
        ...state,
        dateRange: action.payload.newDateRange,
        // Reset the promoted posts table on date range change
        promotedPostsStatsSortKey: initialState.promotedPostsStatsSortKey,
        promotedPostsStatsSortDir: initialState.promotedPostsStatsSortDir,
        promotedPostsStatsOffset: initialState.promotedPostsStatsOffset,
        promotedPostsStatsLimit: initialState.promotedPostsStatsLimit,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.REQUEST_SUMMARY_STATS:
      return {
        ...state,
        isFetchingAggregateStats: true,
        isFetchingTrendStats: true,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_AGGREGATE_STATS:
      return {
        ...state,
        aggregateStats: action.payload.aggregateStats,
        isFetchingAggregateStats: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_TREND_STATS:
      return {
        ...state,
        trendStats: action.payload.trendStats,
        isFetchingTrendStats: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.FETCH_PROMOTED_POSTS:
      return {
        ...state,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_PROMOTED_POSTS_SUCCEEDED:
      return {
        ...state,
        totalPostCount: action.payload.totalPostCount,
        promotedPostsStats: action.payload.promotedPostsStats.map((stat) => ({
          ...stat,
          postData: {
            ...stat.postData,
            parsedStatus: getParsedStatus(
              stat.postData.status,
              stat.postData.endTime,
            ),
          },
        })),
        isFetchingPromotedPostsInRange: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.FETCH_RECENT_PROMOTED_POSTS:
      return {
        ...state,
        isFetchingRecentPromotions: true,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_RECENT_PROMOTED_POSTS_SUCCEEDED:
      return {
        ...state,
        recentPromotedPostsStats: action.payload.recentPromotedPostsStats,
        hasPromotedPosts: action.payload.hasPromotedPosts,
        isFetchingRecentPromotions: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_TRENDS_SELECTED_VALUE:
      return {
        ...state,
        trendSelectedValue: action.payload.trendSelectedValue,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_PROMOTED_POSTS_TABLE_PARAMS:
      return {
        ...state,
        promotedPostsStatsOffset: action.payload.offset,
        promotedPostsStatsLimit: action.payload.limit,
        promotedPostsStatsSortKey: action.payload.sortKey,
        promotedPostsStatsSortDir: action.payload.sortDir,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.DELETE_PROMOTION:
      return {
        ...state,
        promotedPostsStats: state.promotedPostsStats.filter(
          (stat) => action.payload.campaignId === stat.postData.campaignId,
        ),
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_PROMOTION_STATUS:
      return {
        ...state,
        promotedPostsStats: state.promotedPostsStats.map((stat) => ({
          ...stat,
          postData: {
            ...stat.postData,
            status:
              action.payload.campaignId === stat.postData.campaignId
                ? action.payload.promotionStatusResponse.status
                : stat.postData.status,
            parsedStatus:
              action.payload.campaignId === stat.postData.campaignId
                ? getParsedStatus(
                    action.payload.promotionStatusResponse.status,
                    stat.postData.endTime,
                  )
                : stat.postData.parsedStatus,
          },
        })),
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_INSIGHTS_FOR_POST:
      return {
        ...state,
        isUpdatingInsightsForPost: true,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.REQUEST_CURRENTLY_RUNNING:
      return {
        ...state,
        isFetchingRunningAds: true,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_RUNNING_PROMOTION_POST_IDS:
      return {
        ...state,
        runningPromotionPostIds: action.payload.runningPromotionPostIds,
        isUpdatingInsightsForPost: false,
        isFetchingRunningAds: false,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_POST_PROMOTION_STATES:
      return {
        ...state,
        promotionStates: action.payload.promotionStates,
      };
    case PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_GA_ECOMMERCE_CONNECTED:
      return {
        ...state,
        isGaEcommerceConnected: action.payload.isGaEcommerceConnected,
      };
    default:
      return state;
  }
}
